import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";

// Layouts
import DashboardLayout from "./components/layout/DashboardLayout";
import PlatformLayout from "./components/layout/PlatformLayout";

// Auth
import LoginPage from "./pages/LoginPage";

// Tenant Workspace Pages
import DashboardPage from "./pages/DashboardPage";
import DocumentsPage from "./pages/DocumentsPage";
import TimesheetPage from "./pages/TimesheetPage";
import IntakePage from "./pages/IntakePage";
import ReviewPage from "./pages/ReviewQueuePage";
import JobsPage from "./pages/JobsPage";
import SearchPage from "./pages/SearchPage";
import AuditLogsPage from "./pages/AuditLogsPage";
import ContractsPage from "./pages/ContractsPage";
import UsersPage from "./pages/admin/UsersPage";
import DocumentSettingsPage from "./pages/admin/DocumentSettingsPage";

// Platform Admin Pages
import PlatformTenantsPage from "./pages/platform/PlatformTenantsPage";
import PlatformTenantDetailPage from "./pages/platform/PlatformTenantDetailPage";

import "./App.css";

// Route guard: must be logged in
function AuthRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center h-screen text-slate-400">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children || <Outlet />;
}

// Route guard: must be platform admin
function PlatformRoute() {
  const { user, isPlatformAdmin, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (!isPlatformAdmin) return <Navigate to="/dashboard" replace />;
  return (
    <PlatformLayout>
      <Outlet />
    </PlatformLayout>
  );
}

// Route guard: must be a tenant user (NOT platform admin)
function TenantRoute() {
  const { user, isPlatformAdmin, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (isPlatformAdmin) return <Navigate to="/platform/tenants" replace />;
  return (
    <DashboardLayout>
      <Outlet />
    </DashboardLayout>
  );
}

// Route guard: must have a specific module enabled
function ModuleRoute({ moduleKey, children }) {
  const { hasModule, isPlatformAdmin } = useAuth();
  if (!isPlatformAdmin && !hasModule(moduleKey)) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <div className="h-16 w-16 rounded-full bg-slate-100 flex items-center justify-center mb-4">
          <svg className="h-8 w-8 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
        </div>
        <h2 className="text-xl font-semibold text-slate-900">Module Not Enabled</h2>
        <p className="text-slate-500 mt-2">This module is not available for your organization.<br/>Contact your platform administrator.</p>
      </div>
    );
  }
  return children;
}

// Route guard: tenant admin only
function TenantAdminRoute({ children }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "tenant_admin" || user?.role === "super_admin";
  if (!isAdmin) {
    return (
      <div className="text-center py-16">
        <h2 className="text-xl font-semibold text-slate-900">Access Denied</h2>
        <p className="text-slate-500 mt-2">You don't have permission to access this page.</p>
      </div>
    );
  }
  return children;
}

// Smart redirect after login
function LoginRedirect() {
  const { user, isPlatformAdmin, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (isPlatformAdmin) return <Navigate to="/platform/tenants" replace />;
  return <Navigate to="/dashboard" replace />;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          {/* Public */}
          <Route path="/login" element={<LoginPage />} />

          {/* Smart redirect */}
          <Route path="/" element={<LoginRedirect />} />

          {/* Platform Admin routes */}
          <Route element={<PlatformRoute />}>
            <Route path="/platform/tenants" element={<PlatformTenantsPage />} />
            <Route path="/platform/tenants/:tenantId" element={<PlatformTenantDetailPage />} />
          </Route>

          {/* Tenant Workspace routes */}
          <Route element={<TenantRoute />}>
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/documents" element={<DocumentsPage />} />
            <Route path="/review-queue" element={<ReviewPage />} />
            <Route path="/jobs" element={<JobsPage />} />
            <Route path="/search" element={<SearchPage />} />
            <Route
              path="/timesheet"
              element={<ModuleRoute moduleKey="timesheet_ocr"><TimesheetPage /></ModuleRoute>}
            />
            <Route
              path="/intake"
              element={<ModuleRoute moduleKey="accounting_intake"><IntakePage /></ModuleRoute>}
            />
            <Route
              path="/contracts"
              element={<ModuleRoute moduleKey="contracts"><ContractsPage /></ModuleRoute>}
            />
            <Route
              path="/audit-logs"
              element={<TenantAdminRoute><AuditLogsPage /></TenantAdminRoute>}
            />
            <Route
              path="/admin/users"
              element={<TenantAdminRoute><UsersPage /></TenantAdminRoute>}
            />
            <Route
              path="/admin/document-settings"
              element={<TenantAdminRoute><DocumentSettingsPage /></TenantAdminRoute>}
            />
          </Route>

          {/* Catch all */}
          <Route path="*" element={<LoginRedirect />} />
        </Routes>
        <Toaster />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
